import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import ErrorBoundary from '../../hoc/ErrorBoundary';
import { getWebV2Url, getToken, setToken } from '../../services/Common';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    marginTop:"30px",
    height: props => props.height || 'calc(100vh - 89px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'transparent',
  },

  loaderContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10
  },
  alert: {
    margin: theme.spacing(2)
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    overflow: 'auto',
    backgroundColor: 'transparent',
  }
}));

const TOKEN_BACKUP_KEY = '__appv2_token_backup__';
const GUARD_INSTALLED_KEY = '__appv2_guard_installed__';

function installTokenGuard() {
  if (typeof window === 'undefined') return;
  if (window[GUARD_INSTALLED_KEY]) return;
  window[GUARD_INSTALLED_KEY] = true;

  window[TOKEN_BACKUP_KEY] = getToken();

  const desc = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie');
  const origSet = desc && desc.set;
  const origGet = desc && desc.get;
  if (origSet && origGet) {
    Object.defineProperty(document, 'cookie', {
      configurable: true,
      get() {
        const backup = window[TOKEN_BACKUP_KEY];
        if (backup) {
          try {
            const raw = origGet.call(this);
            if (!raw || raw.indexOf('token=') === -1 || /(?:^|;\s*)token=\s*(?:;|$)/.test(raw)) {
              return raw ? (raw + '; token=' + backup) : ('token=' + backup);
            }
          } catch(e) {}
        }
        return origGet.call(this);
      },
      set(val) {
        const str = String(val || '');
        const isToken = /^\s*token\s*=/.test(str);
        const isClear = /^\s*token\s*=\s*(?:;|$)/.test(str) || (isToken && /expires\s*=\s*Thu,\s*01\s+Jan\s+1970/.test(str));
        if (isToken && isClear) {
          const backup = window[TOKEN_BACKUP_KEY];
          if (backup) return;
        }
        if (isToken && !isClear) {
          const m = str.match(/^\s*token\s*=([^;]*)/);
          if (m && m[1]) window[TOKEN_BACKUP_KEY] = m[1];
        }
        return origSet.call(this, val);
      },
    });
  }
}

const AppV2Integration = ({ user, baseUrl = process.env.REACT_APP_V2_URL, path, height, params }) => {
    const classes = useStyles({ height });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [iframeUrl, setIframeUrl] = useState('');
    const intervalRef = useRef(null);

    baseUrl = getWebV2Url();

    useEffect(() => {
        installTokenGuard();

        intervalRef.current = setInterval(() => {
            const backup = window[TOKEN_BACKUP_KEY];
            if (!backup) return;
            const cur = getToken();
            if (cur !== backup) setToken(backup);
        }, 50);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const setupIframe = () => {
            try {
                let targetPath;
                if (path) {
                    targetPath = path;
                } else {
                    // Get current path excluding the base path
                    targetPath = window.location.pathname.replace('/admin/app/v2/', '');
                }
                const searchParams = window.location.search;
                
                // Construct the full URL for the iframe
                // Ensure targetPath starts with / if not present (unless it's empty)
                if (targetPath && !targetPath.startsWith('/')) {
                    targetPath = '/' + targetPath;
                }
                
                let fullUrl = `${baseUrl}${targetPath}`;
                fullUrl = fullUrl + '?email=' + encodeURIComponent(user.email) + '&password=test123456&company=' + encodeURIComponent(user.company) + '&phone=' + encodeURIComponent(user.phone) + '&ownerName=' + encodeURIComponent(user.first_name) + '&loginMode='+ (user.role === 'owner'? 'owner' : 'team') + '&role=' + user.role + '&ownerEmail='+ user.admin;
                setIframeUrl(fullUrl);
            } catch (err) {
                console.error('Error setting up iframe:', err);
                setError('Failed to load the application');
            } finally {
                setLoading(false);
            }
        };

        setupIframe();
    }, [baseUrl, path]);

    const handleIframeLoad = () => {
        setLoading(false);
    };

    const handleIframeError = () => {
        setError('Failed to load the application');
        setLoading(false);
    };

    return (
        <ErrorBoundary name="AppV2Integration">
            <Box className={classes.root + ' appiframeclass'} style={{marginTop:'0px'}}>
                {loading && (
                    <Box className={classes.loaderContainer}>
                        <CircularProgress />
                    </Box>
                )}
                
                {error && (
                    <Alert 
                        severity="error" 
                        className={classes.alert}
                    >
                        {error}
                    </Alert>
                )}

                <iframe
                    src={iframeUrl}
                    className={classes.iframe}
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                    title="App V2 Integration"
                    scrolling="auto"
                    referrerPolicy="no-referrer"
                    loading="lazy"
                />
            </Box>
        </ErrorBoundary>
    );
};

export default AppV2Integration;
